import { create } from 'zustand'
import type { ConsentOnlineItem, ConsentOwnHandsItem, ConsentTextsByIco, Company } from '../utils/Types/ConsentTypes'

export enum ConsentType {
  AGREE = 'agree',
  WITHDRAWAL = 'withdrawal',
  LATER = 'later',
}

export enum ConsentTextType {
  HEADER = "header",
  ONLINE = "online",
  OWN_HANDS = "own_hands",
}

export enum EmailVerifiedState {
  SUBMITTED = "submitted",
  SENT = "sent",
  VERIFIED = "verified",
}

interface ConsentOnlineText {
  id: number
  text: string
  type: string
  validTo: Date
}

interface ConsentStore {
  // New fields for multiple companies support
  companies: Company[]
  setCompanies: (companies: Company[]) => void
  selectedIco: string | null
  setSelectedIco: (ico: string | null) => void
  consentsOnline: ConsentOnlineItem[]
  setConsentsOnline: (consents: ConsentOnlineItem[]) => void
  consentsOwnHands: ConsentOwnHandsItem[]
  setConsentsOwnHands: (consents: ConsentOwnHandsItem[]) => void
  consentTextsByIcos: ConsentTextsByIco[]
  setConsentTextsByIcos: (texts: ConsentTextsByIco[]) => void

  // Getters for current company
  getSelectedConsentOnline: () => ConsentOnlineItem | undefined
  getSelectedConsentOwnHands: () => ConsentOwnHandsItem | undefined
  getSelectedConsentTexts: () => ConsentTextsByIco | undefined

  // Existing fields (for backward compatibility)
  email: string
  setEmail: (email: string) => void
  phone: string
  setPhone: (phone: string) => void
  consentType: ConsentType | null | undefined
  setConsentType: (consentType: ConsentType | null) => void
  consentOnlineText: ConsentOnlineText | null
  setConsentOnlineText: (consentOnlineText: ConsentOnlineText) => void
  consentOwnHandsText: ConsentOnlineText | null
  setConsentOwnHandsText: (consentOwnHandsText: ConsentOnlineText) => void
  consentOnlineCreatedById: number | null
  setConsentOnlineCreatedById: (consentOnlineCreatedById: number) => void
  consentDialogOpen: boolean
  setConsentDialogOpen: (consentDialogOpen: boolean) => void
  consentOnlineDeletedAt: Date | null
  setConsentOnlineDeletedAt: (consentOnlineDeletedAt: Date) => void
  consentOnlineCreatedAt: Date | null
  setConsentOnlineCreatedAt: (consentOnlineCreatedAt: Date) => void
  consentOwnHandsCreatedAt: Date | null
  setConsentOwnHandsCreatedAt: (consentOnlineCreatedAt: Date) => void
  emailVerifyCodeExpires: Date | null
  setEmailVerifyCodeExpires: (emailVerifyCodeExpires: Date) => void
  emailVerifiedState: EmailVerifiedState | null
  setEmailVerifiedState: (emailVerifiedState: EmailVerifiedState) => void
  consentOwnHandsDeletedAt: Date | null
  setConsentOwnHandsDeletedAt: (consentOwnHandsDeletedAt: Date) => void
  consentOwnHandsDeletedById: number | null
  setConsentOwnHandsDeletedById: (consentOwnHandsDeletedById: number) => void
}

const useConsentStore = create<ConsentStore>((set, get) => ({
  // New fields for multiple companies support
  companies: [],
  setCompanies: (companies: Company[]) => set({ companies }),

  selectedIco: null,
  setSelectedIco: (selectedIco: string | null) => set({ selectedIco }),

  consentsOnline: [],
  setConsentsOnline: (consentsOnline: ConsentOnlineItem[]) => set({ consentsOnline }),

  consentsOwnHands: [],
  setConsentsOwnHands: (consentsOwnHands: ConsentOwnHandsItem[]) => set({ consentsOwnHands }),

  consentTextsByIcos: [],
  setConsentTextsByIcos: (consentTextsByIcos: ConsentTextsByIco[]) => set({ consentTextsByIcos }),

  // Getters for current company
  getSelectedConsentOnline: () => {
    const { selectedIco, consentsOnline } = get()
    return consentsOnline.find(c => c.ico === selectedIco)
  },

  getSelectedConsentOwnHands: () => {
    const { selectedIco, consentsOwnHands } = get()
    // Find consent own hands by text.ico
    return consentsOwnHands.find(c => c.text?.ico === selectedIco)
  },

  getSelectedConsentTexts: () => {
    const { selectedIco, consentTextsByIcos } = get()
    return consentTextsByIcos.find(c => c.ico === selectedIco)
  },

  // Existing fields (for backward compatibility)
  email: '',
  setEmail: (email: string) => set({ email }),

  phone: '',
  setPhone: (phone: string) => set({ phone }),

  consentType: undefined,
  setConsentType: (consentType: ConsentType | null) => set({ consentType }),

  consentOnlineText: null,
  setConsentOnlineText: (consentOnlineText: ConsentOnlineText) => set({ consentOnlineText }),

  consentOwnHandsText: null,
  setConsentOwnHandsText: (consentOwnHandsText: ConsentOnlineText) => set({ consentOwnHandsText }),

  consentOnlineCreatedById: null,
  setConsentOnlineCreatedById: (consentOnlineCreatedById: number) => { set({ consentOnlineCreatedById }) },

  consentDialogOpen: false,
  setConsentDialogOpen: (consentDialogOpen: boolean) => set({ consentDialogOpen }),

  consentOnlineDeletedAt: null,
  setConsentOnlineDeletedAt: (consentOnlineDeletedAt: Date) => set({ consentOnlineDeletedAt }),

  consentOnlineCreatedAt: null,
  setConsentOnlineCreatedAt: (consentOnlineCreatedAt: Date) => set({ consentOnlineCreatedAt }),

  consentOwnHandsCreatedAt: null,
  setConsentOwnHandsCreatedAt: (consentOwnHandsCreatedAt: Date) => set({ consentOwnHandsCreatedAt }),

  emailVerifyCodeExpires: null,
  setEmailVerifyCodeExpires: (emailVerifyCodeExpires: Date) => set({ emailVerifyCodeExpires }),

  emailVerifiedState: null,
  setEmailVerifiedState: (emailVerifiedState: EmailVerifiedState) => set({ emailVerifiedState }),

  consentOwnHandsDeletedAt: null,
  setConsentOwnHandsDeletedAt: (consentOwnHandsDeletedAt: Date) => set({ consentOwnHandsDeletedAt }),

  consentOwnHandsDeletedById: null,
  setConsentOwnHandsDeletedById: (consentOwnHandsDeletedById: number) => set({ consentOwnHandsDeletedById }),
}))

export default useConsentStore
